/**
 * 네이버 전환추적 (검색광고 프리미엄 로그분석, wcs).
 *
 * 네이버는 서버 전환 API 가 없어 브라우저 발화가 전부다. 환경변수 VITE_NAVER_WCS_ID(광고관리시스템 공통 스크립트의
 * 계정 ID, `wa`)가 비어 있으면 아무것도 넣지 않는다 — dev·로컬 트래픽이 광고 학습을 오염시키지 않게.
 *
 * - SPA 라우트 전환에는 페이지가 다시 로드되지 않으므로 경로가 바뀔 때마다 PV(wcs_do)를 직접 쏜다.
 * - 스크립트가 아직 안 붙었을 때 들어온 전환은 큐에 두었다가 로드 뒤 흘려보낸다.
 * - 구 wcs.cnv 와 신 wcs.trans 를 같이 쓰지 않는다. 전환은 naverTrans() 로만.
 */

const WCS_ID: string = (import.meta.env.VITE_NAVER_WCS_ID as string | undefined)?.trim() || '';
const SCRIPT_SRC = 'https://wcs.naver.net/wcslog.js';

export type NaverConvType = 'sign_up' | 'lead' | 'purchase' | 'view_product' | 'custom001' | 'custom002' | 'custom003';
export interface NaverItem {
    id: string;
    name: string;
    quantity: number;
    payAmount: number;
}

type Wcs = {
    inflow?: (domain: string) => void;
    trans?: (conv: { type: string; value?: string; items?: NaverItem[] }) => void;
};
declare global {
    interface Window {
        wcs?: Wcs;
        wcs_add?: Record<string, string>;
        wcs_do?: (arg?: Record<string, string>) => void;
    }
}

const pending: (() => void)[] = [];
let loaded = false;
let lastPath = '';

function ready(): boolean {
    return !!WCS_ID && typeof window !== 'undefined' && !!window.wcs;
}

function initAccount(): void {
    window.wcs_add = window.wcs_add ?? {};
    window.wcs_add.wa = WCS_ID;
}

/** 스크립트 삽입. 앱 렌더 전에 한 번 부른다. ID 가 없으면 no-op */
export function initNaverWcs(): void {
    if (!WCS_ID || typeof window === 'undefined') return;
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = SCRIPT_SRC;
    s.onload = () => {
        loaded = true;
        naverPageView(window.location.pathname, true);
        while (pending.length) pending.shift()?.();
    };
    document.head.appendChild(s);
}

/** 경로가 바뀔 때마다 PV 1회. 같은 경로 반복 호출은 무시 */
export function naverPageView(path: string, force = false): void {
    try {
        if (!ready() || !loaded) return;
        if (!force && path === lastPath) return;
        lastPath = path;
        initAccount();
        window.wcs!.inflow?.(window.location.hostname);
        window.wcs_do?.();
    } catch {
        /* 통계 오류가 화면을 막지 않게 */
    }
}

/** 전환 발화. purchase 는 value(원화) 필수. 문서 규격: type 문자열, value 는 문자열 */
export function naverTrans(type: NaverConvType, opts?: { value?: number; items?: NaverItem[] }): void {
    if (!WCS_ID || typeof window === 'undefined') return;
    const send = () => {
        try {
            if (!ready()) return;
            initAccount();
            // 상품명에 따옴표 금지 (네이버 규격)
            const items = opts?.items?.map((i) => ({ ...i, name: i.name.replace(/["']/g, '') }));
            window.wcs!.trans?.({
                type,
                ...(opts?.value != null ? { value: String(Math.round(opts.value)) } : {}),
                ...(items ? { items } : {}),
            });
        } catch {
            /* noop */
        }
    };
    if (loaded && ready()) send();
    else pending.push(send);
}
